import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useEffect, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {
    Body,
    LabelSmall,
    LabelXSmall,
} from "@khanacademy/wonder-blocks-typography";
import {isIFrame} from "@khan/guide-core-helpers";
import type {AutosavingStatus} from "@khan/guide-ui";
import {RequestInfo} from "@khan/shared-core";

import {PARENT_KHANMIGO_TOOLS_PATH} from "#page-package/util.ts";

import {Tabs} from "./ai-tools-and-docs.tsx";
import {Platform} from "./utils.ts";

import cloudCheckIcon from "@phosphor-icons/core/regular/cloud-check.svg";
import cloudErrorIcon from "@phosphor-icons/core/regular/cloud-warning.svg";

type Props = {
    status: AutosavingStatus;
    path: string;
};

// Custom hook for image preloading. Mainly used for the error icon in case of
// autosave failure
function usePreloadImage(src: string) {
    useEffect(() => {
        const image = new Image();
        image.src = src;
    }, [src]);
}

function TooltipContent({
    label,
    lastSaved,
}: {
    label: string;
    lastSaved: number | null;
}) {
    return (
        <View style={styles.content}>
            <Body>{label}</Body>
            {lastSaved && (
                <LabelXSmall>
                    {new Date(lastSaved).toLocaleString(RequestInfo.kaLocale, {
                        year: "2-digit",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                    })}
                </LabelXSmall>
            )}
        </View>
    );
}

function StatusIconButton({
    status,
    label,
}: {
    status: AutosavingStatus;
    label: string;
}) {
    const icon = status === "error" ? cloudErrorIcon : cloudCheckIcon;
    return (
        <IconButton
            testId="autosave-indicator"
            icon={icon}
            kind="tertiary"
            size="small"
            href="/teacher/resources/documents"
            target="_blank"
            aria-label={label}
            actionType={status === "error" ? "destructive" : "neutral"}
        />
    );
}

export default function AutosaveIndicator({status, path}: Props) {
    const [lastSaved, setLastSaved] = useState<number | null>(null);
    usePreloadImage(cloudErrorIcon);

    useEffect(() => {
        switch (status) {
            case "loading":
                return;
            case "saved":
                setLastSaved(Date.now());
                break;
            default:
                setLastSaved(null);
        }
    }, [status]);

    if (status === "idle") {
        return null;
    }

    const determineMyDocumentsPath = (): string => {
        if (isIFrame()) {
            return `/teacher/khanmigo-tools-canvas?platform=${Platform.Canvas}&tab=${Tabs.DOCUMENTS}`;
        }

        if (path.includes(PARENT_KHANMIGO_TOOLS_PATH)) {
            return `${PARENT_KHANMIGO_TOOLS_PATH}?platform=${Platform.KhanAcademy}&tab=${Tabs.DOCUMENTS}`;
        }

        return "/teacher/resources/documents";
    };

    const label =
        status === "error" ? t`Failed to autosave` : t`Saved automatically`;

    return (
        <View style={styles.wrapper}>
            <Tooltip
                content={<TooltipContent label={label} lastSaved={lastSaved} />}
            >
                <StatusIconButton status={status} label={label} />
            </Tooltip>
            <Strut size={spacing.xSmall_8} />
            {status === "error" ? (
                <View style={styles.errorLabel}>{label}</View>
            ) : lastSaved ? (
                <LabelSmall>
                    {t`Saved to`}{" "}
                    <Link
                        href={determineMyDocumentsPath()}
                        target={isIFrame() ? undefined : "_blank"}
                        testId="my-documents-link"
                    >
                        {t`My Documents`}
                    </Link>
                </LabelSmall>
            ) : null}
        </View>
    );
}

const styles = StyleSheet.create({
    wrapper: {
        marginLeft: spacing.large_24,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    },
    content: {
        padding: `${spacing.small_12}px ${spacing.medium_16}px`,
    },
    errorLabel: {
        color: color.red,
    },
});
